import type { Stage } from '@/domain/creature/types';
import type { CareEvent } from '@/domain/care/types';
import type { CareTier } from '@/domain/care/evolution';
import type { FoodId } from '@/domain/care/foods';
import type { ToyId } from '@/domain/care/toys';
import { eventBus } from '@/infra/event-bus/EventBus';

declare module '@/infra/event-bus/types' {
  interface EventMap {
    'creature.tick': { creatureId: string };
    'creature.stage-changed': { creatureId: string; from: Stage; to: Stage };
    'creature.died': { creatureId: string; cause: 'neglect' | 'old-age' | 'expedition' };
    'creature.hungry': { creatureId: string };
    'creature.hunger-restored': { creatureId: string };
    'creature.unhappy': { creatureId: string };
    'creature.happiness-restored': { creatureId: string };
    'creature.tantrum-start': { creatureId: string };
    'creature.tantrum-end': { creatureId: string };
    'creature.sick-start': { creatureId: string };
    'creature.cured': { creatureId: string };
    'creature.poop-spawned': { creatureId: string };
    'creature.evolved': { creatureId: string; tier: CareTier; scoreFinal: number };
    'creature.fed': { creatureId: string; foodId: FoodId };
    'creature.played': { creatureId: string; toyId: ToyId };
    'creature.cleaned': { creatureId: string };
    'creature.disciplined': { creatureId: string; correct: boolean };
    'creature.healed': { creatureId: string };
    'creature.refused': {
      creatureId: string;
      action: 'feed' | 'play' | 'clean' | 'discipline' | 'heal';
      severity: 'tantrum' | 'mild';
      reason?: 'cooldown' | 'not-needed' | 'tantrum';
    };
    'creature.egg-wiggle': { creatureId: string };
    'creature.hatched': { creatureId: string };
  }
}

export function emitCareEvent(creatureId: string, e: CareEvent): void {
  switch (e.kind) {
    case 'tick':
      eventBus.emit('creature.tick', { creatureId });
      break;
    case 'stage-changed':
      eventBus.emit('creature.stage-changed', { creatureId, from: e.from, to: e.to });
      break;
    case 'died':
      eventBus.emit('creature.died', { creatureId, cause: e.cause });
      break;
    case 'hungry':
      eventBus.emit('creature.hungry', { creatureId });
      break;
    case 'hunger-restored':
      eventBus.emit('creature.hunger-restored', { creatureId });
      break;
    case 'unhappy':
      eventBus.emit('creature.unhappy', { creatureId });
      break;
    case 'happiness-restored':
      eventBus.emit('creature.happiness-restored', { creatureId });
      break;
    case 'tantrum-start':
      eventBus.emit('creature.tantrum-start', { creatureId });
      break;
    case 'tantrum-end':
      eventBus.emit('creature.tantrum-end', { creatureId });
      break;
    case 'sick-start':
      eventBus.emit('creature.sick-start', { creatureId });
      break;
    case 'cured':
      eventBus.emit('creature.cured', { creatureId });
      break;
    case 'poop-spawned':
      eventBus.emit('creature.poop-spawned', { creatureId });
      break;
    case 'evolved':
      eventBus.emit('creature.evolved', { creatureId, tier: e.tier, scoreFinal: e.scoreFinal });
      break;
  }
}
